import type { CSSProperties, ReactNode } from "react";
import { Icon, type IconName } from "../../../shared/ui";
import type { PendingKind, Sparkline as SparklineData } from "./data";
import "./kit.css";

// Lightweight port of the ds components the «Qanday ishlaydi» screens use
// (docs/design/landing-v4/prototype/ds/ds-components.jsx), only with the props those screens need.
// Everything is a decorative span/div — no button/input/link — because the phone is aria-hidden.
// Sizes, colours and strokes live in kit.css (`hk-*`); icons come from the shared sprite.

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

// ---- Buttons --------------------------------------------------------------------------------

interface ButtonProps {
  variant?: "primary" | "secondary" | "ghost";
  sm?: boolean;
  icon?: IconName;
  disabled?: boolean;
  /** Scripted press (scale to --press-scale), like a finger on the button. */
  pressed?: boolean;
  /** flex: 1 inside a row (footer, sheet buttons). */
  grow?: boolean;
  /** Square 52px icon-only button (footer leading slot). */
  square?: boolean;
  children?: ReactNode;
}

export function Button({ variant = "primary", sm, icon, disabled, pressed, grow, square, children }: ButtonProps) {
  return (
    <span className={cx("hk-btn", variant, sm && "sm", disabled && "off", pressed && "hk-pressed", grow && "hk-btn-grow", square && "sq")}>
      {icon && <Icon name={icon} />}
      {children}
    </span>
  );
}

export function IconButton({ icon }: { icon: IconName }) {
  return (
    <span className="hk-ibtn">
      <Icon name={icon} />
    </span>
  );
}

// ---- Shell: app bar, stepper, footer ---------------------------------------------------------

interface AppBarProps {
  title: string;
  sub?: string;
  trailing?: ReactNode;
  /** Bottom hairline (off inside the Create header, which draws its own). */
  border?: boolean;
}

/** Safe-top app bar: back, centred title + sub, trailing slot. */
export function AppBar({ title, sub, trailing, border = true }: AppBarProps) {
  return (
    <div className={cx("hk-appbar", border && "bd")}>
      <div className="hk-appbar-row">
        <span className="hk-appbar-back">
          <Icon name="i-back" />
        </span>
        <div className="hk-appbar-c">
          <div className="hk-appbar-t">{title}</div>
          {sub && <div className="hk-appbar-s">{sub}</div>}
        </div>
        <span className="hk-appbar-tr">{trailing}</span>
      </div>
    </div>
  );
}

/** 4-step progress; `current` is 1-based. Done steps get a check, the current bar grows in. */
export function Stepper({ steps, current }: { steps: readonly string[]; current: number }) {
  return (
    <div className="hk-stepper">
      {steps.map((label, i) => {
        const st = i + 1 < current ? "done" : i + 1 === current ? "cur" : "next";
        return (
          <div key={label} className={"hk-step " + st}>
            <div className="hk-step-bar">{st !== "next" && <span />}</div>
            <div className="hk-step-l">
              {st === "done" && <Icon name="i-check" />}
              <span>{label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** "Oylik to‘lov ........ $800 × 12" line above the footer buttons. */
export function FooterSummary({ label, value }: { label: string; value: string }) {
  return (
    <div className="hk-footer-sum">
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}

interface StickyFooterProps {
  summary?: ReactNode;
  /** Replaces the default back square (e.g. a secondary bell button). */
  leading?: ReactNode;
  /** Show the 52px back square when there is no `leading`. */
  back?: boolean;
  children: ReactNode;
}

export function StickyFooter({ summary, leading, back, children }: StickyFooterProps) {
  return (
    <div className="hk-footer">
      {summary}
      <div className="hk-footer-row">
        {leading ??
          (back && (
            <span className="hk-footer-back">
              <Icon name="i-back" />
            </span>
          ))}
        {children}
      </div>
    </div>
  );
}

// ---- Form -------------------------------------------------------------------------------------

function FieldLabel({ label, req, optional }: { label: string; req?: boolean; optional?: string }) {
  return (
    <div className="hk-label">
      <span>
        {label}
        {req && <b>*</b>}
      </span>
      {optional && <span className="opt">· {optional}</span>}
    </div>
  );
}

interface TextFieldProps {
  label: string;
  req?: boolean;
  optional?: string;
  /** Amount field: taller box, 800-weight value. */
  lg?: boolean;
  prefix?: string;
  /** Empty → the placeholder in --text-faint. */
  value?: string;
  placeholder?: string;
}

/** Display-only field (no input): label row + bordered box. */
export function TextField({ label, req, optional, lg, prefix, value, placeholder }: TextFieldProps) {
  return (
    <div className="hk-field">
      <FieldLabel label={label} req={req} optional={optional} />
      <div className={cx("hk-field-box", lg && "lg")}>
        {prefix && <span className="hk-field-pre">{prefix}</span>}
        <span className={cx("hk-field-v", !value && "ph")}>{value || placeholder}</span>
      </div>
    </div>
  );
}

export function Segmented({ items, on }: { items: readonly string[]; on: number }) {
  return (
    <div className="hk-seg">
      {items.map((label, i) => (
        <span key={label} className={cx("hk-seg-i", i === on && "on")}>
          {label}
        </span>
      ))}
    </div>
  );
}

interface PickFieldProps {
  label: string;
  req?: boolean;
  icon: IconName;
  value: string;
}

export function PickField({ label, req, icon, value }: PickFieldProps) {
  return (
    <div className="hk-field">
      <FieldLabel label={label} req={req} />
      <div className="hk-pick">
        <Icon name={icon} />
        <span className="hk-pick-v">{value}</span>
        <Icon name="i-chevDown" className="ic hk-pick-chev" />
      </div>
    </div>
  );
}

export function SectionLabel({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="hk-sec">
      <span className="hk-sec-t">{title}</span>
      {sub && <span className="hk-sec-s">{sub}</span>}
    </div>
  );
}

// ---- Cards & rows -----------------------------------------------------------------------------

interface AppCardProps {
  title?: string;
  trailing?: ReactNode;
  danger?: boolean;
  /** Layout modifiers from kit.css: "tight" (gap 0), "compact" (padding 12). */
  className?: string;
  children: ReactNode;
}

export function AppCard({ title, trailing, danger, className, children }: AppCardProps) {
  return (
    <div className={cx("hk-card", danger && "danger", className)}>
      {(title || trailing) && (
        <div className="hk-card-h">
          {title && <span className="hk-card-t">{title}</span>}
          {trailing}
        </div>
      )}
      {children}
    </div>
  );
}

interface StatRowProps {
  label: string;
  value: string;
  tone?: "positive" | "negative";
  /** Total row: top rule, bolder label and value. */
  total?: boolean;
  /** Leading 8px status dot (schedule rows). */
  dot?: "paid" | "late" | "due";
  divider?: boolean;
}

export function StatRow({ label, value, tone, total, dot, divider = true }: StatRowProps) {
  return (
    <div className={cx("hk-stat", total && "total", !total && divider && "div", dot && "has-dot")}>
      {dot && <span className={"hk-stat-dot " + dot} />}
      <span className="hk-stat-l">{label}</span>
      <span className={cx("hk-stat-v", tone)}>{value}</span>
    </div>
  );
}

interface BannerProps {
  tone: "info" | "danger";
  title?: string;
  children: ReactNode;
  action?: string;
  /** Scripted press on the action. */
  pressed?: boolean;
}

export function Banner({ tone, title, children, action, pressed }: BannerProps) {
  return (
    <div className={"hk-banner " + tone}>
      <Icon name={tone === "danger" ? "i-alert" : "i-info"} />
      <div className="hk-banner-x">
        {title && <div className="hk-banner-t">{title}</div>}
        <div className="hk-banner-b">{children}</div>
      </div>
      {action && <span className={cx("hk-banner-a", pressed && "hk-pressed")}>{action}</span>}
    </div>
  );
}

export function DashedBox({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cx("hk-dashed", className)}>{children}</div>;
}

/** 72px striped photo placeholder with a remove badge, or the dashed "add" tile. */
export function PhotoThumb({ label, add }: { label: string; add?: boolean }) {
  if (add) {
    return (
      <div className="hk-thumb add">
        <Icon name="i-camera" />
        {label}
      </div>
    );
  }
  return (
    <div className="hk-thumb">
      <span className="hk-thumb-l">{label}</span>
      <span className="hk-thumb-x">
        <Icon name="i-x" />
      </span>
    </div>
  );
}

export function PaymentRow({ title, sub, amount }: { title: string; sub: string; amount: string }) {
  return (
    <div className="hk-pay">
      <span className="hk-pay-ic">
        <Icon name="i-arrowDown" />
      </span>
      <div className="hk-pay-x">
        <div className="hk-pay-t">{title}</div>
        <div className="hk-pay-s">{sub}</div>
      </div>
      <span className="hk-pay-a">+ {amount}</span>
    </div>
  );
}

// ---- Small atoms --------------------------------------------------------------------------------

/** Round initials on the green hero (onGreen, 42px). */
export function Avatar({ initials }: { initials: string }) {
  return <span className="hk-av">{initials}</span>;
}

export function StatusBadge({ label }: { label: string }) {
  return (
    <span className="hk-badge">
      <i />
      {label}
    </span>
  );
}

export function ProgressBar({ value, overdue }: { value: number; overdue?: boolean }) {
  return (
    <div className="hk-prog">
      <span className={cx("ns-grow-x", overdue && "over")} style={{ width: `${Math.round(value * 1000) / 10}%` }} />
    </div>
  );
}

type MoneySize = "hero" | "kpi" | "lg" | "row" | "md" | "sm";

export function Money({ value, size = "md", tone, className }: { value: string; size?: MoneySize; tone?: "positive" | "negative"; className?: string }) {
  return <span className={cx("hk-money", "hk-money-" + size, tone, className)}>{value}</span>;
}

/** Agreement-type tile (technology → device). */
export function TypeIcon() {
  return (
    <span className="hk-type">
      <Icon name="i-device" />
    </span>
  );
}

/** White trend pill on the green hero. */
export function TrendChip({ label }: { label: string }) {
  return (
    <span className="hk-trend">
      <svg width="10" height="11" viewBox="0 0 10 11" aria-hidden="true">
        <path d="M5 10V1M1.5 4.5L5 1l3.5 3.5" />
      </svg>
      {label}
    </span>
  );
}

/** Hero sparkline: the line draws, the area fades in, the end dot pops (CSS only). */
export function Sparkline({ data }: { data: SparklineData }) {
  return (
    <svg className="hk-spark" width={data.width} height={data.height} viewBox={`0 0 ${data.width} ${data.height}`} aria-hidden="true">
      <defs>
        {/* Fixed id: one sparkline per page (Home screen only). */}
        <linearGradient id="hk-spark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.28" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path className="hk-spark-area" d={data.area} fill="url(#hk-spark)" />
      <path className="hk-spark-ln" d={data.line} pathLength={1} />
      <circle className="hk-spark-dot" cx={data.end.x} cy={data.end.y} r={3.5} />
    </svg>
  );
}

// ---- Home blocks ------------------------------------------------------------------------------

export interface QuickAction {
  icon: IconName;
  label: string;
  badge?: number;
}

export function QuickActions({ items }: { items: readonly QuickAction[] }) {
  return (
    <div className="hk-qa">
      {items.map((it) => (
        <div key={it.icon} className="hk-qa-i">
          <span className="hk-qa-ic">
            <Icon name={it.icon} />
            {it.badge != null && <span className="hk-qa-b">{it.badge}</span>}
          </span>
          <span className="hk-qa-l">{it.label}</span>
        </div>
      ))}
    </div>
  );
}

export interface TabItem {
  icon: IconName;
  label: string;
  badge?: boolean;
}

export function TabBar({ items, on }: { items: readonly TabItem[]; on: number }) {
  return (
    <div className="hk-tabs">
      {items.map((it, i) => (
        <div key={it.icon} className={cx("hk-tab", i === on && "on")}>
          <span className="hk-tab-ic">
            <Icon name={it.icon} />
            {it.badge && (
              <span className="hk-tab-b">
                <i />
              </span>
            )}
          </span>
          <span className="hk-tab-l">{it.label}</span>
        </div>
      ))}
    </div>
  );
}

/** One card of the pending stack, already localized by the screen. */
export interface StackCard {
  name: string;
  product: string;
  /** Time / short date shown top-right. */
  when: string;
  amount: string;
  kind: PendingKind;
  /** "To‘lov keldi" / "Kechikkan: 6 kun" / "2 kundan keyin". */
  status: string;
}

interface PendingStackProps {
  title: string;
  allLabel: string;
  /** Every pending item (front = `index`). Up to 3 from `index` are drawn. */
  cards: readonly StackCard[];
  index: number;
  /** The front card is flying out to the right (360 ms), stamped with `stamp`. */
  flying?: boolean;
  stamp?: string;
  labels: { accept: string; reject: string; remind: string };
}

const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const KIND_CLASS: Record<PendingKind, string> = { in: "in", over: "over", up: "up" };

/**
 * ds PendingStack (1155–1226) without drag: 3 stacked cards (+11px / −4.5% per card). Keys are the
 * absolute item index, so after `index` advances the next card slides into the front slot.
 */
export function PendingStack({ title, allLabel, cards, index, flying, stamp, labels }: PendingStackProps) {
  const left = Math.max(0, cards.length - index);
  return (
    <div className="hk-ps">
      <div className="hk-ps-h">
        <span className="hk-ps-t">{title}</span>
        <span className={cx("hk-ps-n", left === 0 && "zero")}>{left}</span>
        <span className="hk-ps-sp" />
        <span className="hk-ps-all">
          {allLabel}
          <Icon name="i-chevR" />
        </span>
      </div>
      <div className="hk-ps-area">
        {cards.slice(index, index + 3).map((it, k) => {
          const fly = k === 0 && flying;
          const style: CSSProperties = {
            zIndex: 3 - k,
            // ds: translateX(tx) rotate(tx / 22 deg), tx = 420 while flying.
            transform: `translateY(${k * 11}px) scale(${+(1 - k * 0.045).toFixed(3)}) ` + (fly ? "translateX(420px) rotate(19.09deg)" : "translateX(0px) rotate(0deg)"),
            opacity: fly ? 0 : 1,
          };
          const acc = it.kind === "in";
          return (
            <div key={index + k} className="hk-ps-w" style={style}>
              <div className="hk-pc">
                <div className="hk-pc-top">
                  <span className={"hk-pc-av " + KIND_CLASS[it.kind]}>
                    {initials(it.name)}
                    <span className="hk-pc-mini">
                      <Icon name={acc ? "i-arrowDown" : "i-clock"} />
                    </span>
                  </span>
                  <div className="hk-pc-who">
                    <div className="hk-pc-nm">{it.name}</div>
                    <div className="hk-pc-pr">{it.product}</div>
                  </div>
                  <span className="hk-pc-tm">{it.when}</span>
                </div>
                <div className="hk-pc-amt">
                  <span className="hk-pc-sum">{it.amount}</span>
                  <span className={cx("hk-pc-st", it.kind === "over" && "over")}>{it.status}</span>
                </div>
                <div className="hk-pc-btns">
                  {acc ? (
                    <>
                      <span className="hk-pcb rej">{labels.reject}</span>
                      <span className="hk-pcb acc">
                        <Icon name="i-check" />
                        {labels.accept}
                      </span>
                    </>
                  ) : (
                    <span className="hk-pcb one">
                      <Icon name="i-bell" />
                      {labels.remind}
                    </span>
                  )}
                </div>
                {fly && stamp && <span className="hk-pc-stamp ns-digit-in">{stamp}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Overlays ---------------------------------------------------------------------------------

interface BottomSheetProps {
  title: string;
  sub?: string;
  /** Closing: the sheet slides down and the scrim fades (unmount after --dur-sheet). */
  closing?: boolean;
  children: ReactNode;
}

export function BottomSheet({ title, sub, closing, children }: BottomSheetProps) {
  return (
    <div className={cx("hk-sheet-w", closing && "closing")}>
      <div className="hk-scrim ns-scrim-in" />
      <div className="hk-sheet ns-sheet-up">
        <div className="hk-sheet-handle" />
        <div className="hk-sheet-h">
          <div className="hk-sheet-x">
            <div className="hk-sheet-t">{title}</div>
            {sub && <div className="hk-sheet-s">{sub}</div>}
          </div>
          <span className="hk-sheet-close">
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </span>
        </div>
        <div className="hk-sheet-b">{children}</div>
      </div>
    </div>
  );
}

export function Toast({ message }: { message: string }) {
  return (
    <div className="hk-toast ns-toast-in">
      <span className="hk-toast-d">
        <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 12.5l5.5 5.5L20 6.5" />
        </svg>
      </span>
      <span className="hk-toast-m">{message}</span>
    </div>
  );
}

/** One pen stroke, already in pad pixels; drawn by a CSS dash animation. */
export interface SignStroke {
  d: string;
  /** Draw duration, ms. */
  dur: number;
  /** Start delay from the pad's mount, ms. */
  delay: number;
}

interface SignaturePadProps {
  hint: string;
  placeholder: string;
  clearLabel: string;
  sendLabel: string;
  /** Pad drawing area in px (SVG viewBox). */
  width: number;
  height: number;
  strokes: readonly SignStroke[];
  /** First pen move happened: grey border, no placeholder, "send" enabled. */
  drawn: boolean;
  sendPressed?: boolean;
}

export function SignaturePad({ hint, placeholder, clearLabel, sendLabel, width, height, strokes, drawn, sendPressed }: SignaturePadProps) {
  return (
    <div className="hk-sig">
      <div className="hk-sig-hint">{hint}</div>
      <div className={cx("hk-sig-pad", drawn && "drawn")}>
        {!drawn && (
          <div className="hk-sig-ph">
            <Icon name="i-pen" />
            {placeholder}
          </div>
        )}
        <svg className="hk-sig-ink" viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
          {strokes.map((s) => (
            <path key={s.delay} d={s.d} pathLength={1} style={{ "--d": `${s.dur}ms`, "--dl": `${s.delay}ms` } as CSSProperties} />
          ))}
        </svg>
      </div>
      <div className="hk-sig-btns">
        <Button variant="secondary" grow>
          {clearLabel}
        </Button>
        <Button grow disabled={!drawn} pressed={sendPressed}>
          {sendLabel}
        </Button>
      </div>
    </div>
  );
}
